import { isUuid } from '@/lib/validation';
import type { ServiceClient } from '../http/client';
import { ApiError } from '../http/errors';
export type Resource = 'properties' | 'room-types' | 'rooms' | 'units';
export const kinds = {
  properties: 'PROPERTY',
  'room-types': 'ROOM_TYPE',
  rooms: 'ROOM',
  units: 'UNIT',
} as const;
export const purposes = {
  DISPLAY_NAME: '표시 이름',
  FULL_NAME: '전체 이름',
  BRAND_NAME: '브랜드 이름',
  DESCRIPTION: '설명',
  TAGLINE: '한 줄 소개',
  CARD_DESCRIPTION: '카드 소개',
  NEIGHBORHOOD_TITLE: '주변 안내 제목',
  NEIGHBORHOOD_DESCRIPTION: '주변 안내',
  BUILDING_OVERVIEW: '건물 소개',
  NOTICE_SELECT: '선택 안내',
  NOTICE_CONFIRM: '확인 안내',
  SOURCE_NOTICE: '안내',
  ADDRESS: '주소',
  PARKING_INFO: '주차 안내',
  CARD_TYPE: '카드 유형',
  CARD_CONTRACT_LABEL: '계약 표시',
  DISTRICT_LABEL: '지역 표시',
  PROMOTION_LABEL: '프로모션 표시',
  COMMON_AREA_DESCRIPTION: '공용공간 소개',
  COMPONENT_NAME: '공간 이름',
  COMPONENT_DESCRIPTION: '공간 설명',
  LOCATION_LABEL: '위치 안내',
  STATION_NAME: '역 이름',
} as const;
export type Purpose = keyof typeof purposes;
export interface TextValue {
  purpose: Purpose;
  language: 'ko' | 'en' | 'und';
  item_key: string;
  value: string | null;
  source_order: number | null;
  component_key?: string | null;
  group_ref?: unknown;
  media_ref?: unknown;
  source_item_key?: string | null;
}
export interface Component {
  key: string | null;
  kind: 'COMMON_AREA' | 'LOCATION_BULLET' | 'LOCATION_LINE' | 'FLOOR_DIRECTORY';
  parent_key: string | null;
  source_order: number | null;
  provenance: { source_table: string; record_key: string } | null;
  line_code?: string | null;
  is_active?: boolean | null;
  show_on_detail?: boolean | null;
  show_on_search?: boolean | null;
}
export interface Expected {
  owner_version: number;
  presentation_id: string | null;
  presentation_revision: number | null;
}
export interface PresentationCurrent {
  target_kind: (typeof kinds)[Resource];
  target_id: string;
  native_name: string | null;
  publication: 'APPLY_CURRENT';
  expected: Expected;
  texts: TextValue[];
  components: Component[];
  features?: Record<
    string,
    { present: boolean; value: Record<string, string | null> | string[] | null }
  >;
  native_change: { actor_user_id: string; reason: string } | null;
  presentation_change: { actor_user_id: string; reason: string } | null;
  impact: { visible_media_assignments: number; component_reference_collections: number };
}
export interface TextPatch {
  operation: 'SET' | 'REMOVE';
  identity: {
    purpose: Purpose;
    language: TextValue['language'];
    item_key: string;
    component_key: string | null;
    group_ref: null;
    media_ref: null;
  };
  value: TextValue | null;
}
export interface PresentationBody {
  command_id: string;
  expected: Expected;
  values: {
    native_name: { operation: 'SET'; value: string } | null;
    presentation: {
      texts: TextPatch[];
      components: { operation: 'SET' | 'REMOVE'; key: string | null; value: Component | null }[];
    };
  };
  reason: string;
  publication: 'APPLY_CURRENT';
}
export const invalidPresentation = () =>
  new ApiError({
    kind: 'invalid-response',
    service: 'core',
    message: '현재 편집 자료를 확인하지 못했습니다. 다시 조회해 주세요.',
  });
const record = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v);
const exact = (v: Record<string, unknown>, keys: string[]) =>
  Object.keys(v).length === keys.length && keys.every((k) => k in v);
const integer = (v: unknown) => Number.isSafeInteger(v) && Number(v) > 0;
export function parsePresentation(v: unknown, resource: Resource, id: string): PresentationCurrent {
  if (
    !record(v) ||
    !exact(v, [
      'target_kind',
      'target_id',
      'native_name',
      'publication',
      'expected',
      'texts',
      'components',
      'native_change',
      'presentation_change',
      'impact',
      ...('features' in v ? ['features'] : []),
    ]) ||
    v.target_id !== id ||
    v.target_kind !== kinds[resource] ||
    (v.native_name !== null && typeof v.native_name !== 'string') ||
    v.publication !== 'APPLY_CURRENT' ||
    !record(v.expected) ||
    !exact(v.expected, ['owner_version', 'presentation_id', 'presentation_revision'])
  )
    throw invalidPresentation();
  if ('features' in v) {
    const allowed =
      resource === 'properties'
        ? ['coordinates']
        : resource === 'room-types'
          ? ['furniture', 'facilities']
          : [];
    if (
      !record(v.features) ||
      !exact(v.features, allowed) ||
      !Object.values(v.features).every(
        (f) =>
          record(f) &&
          exact(f, ['present', 'value']) &&
          typeof f.present === 'boolean' &&
          (f.value === null ||
            (Array.isArray(f.value)
              ? f.value.every((item) => typeof item === 'string')
              : record(f.value) &&
                Object.values(f.value).every((item) => item === null || typeof item === 'string'))),
      )
    )
      throw invalidPresentation();
  }
  const e = v.expected;
  if (
    !integer(e.owner_version) ||
    !(e.presentation_revision === null || integer(e.presentation_revision)) ||
    !(e.presentation_id === null || isUuid(e.presentation_id)) ||
    (e.presentation_id === null) !== (e.presentation_revision === null) ||
    !Array.isArray(v.texts) ||
    !Array.isArray(v.components)
  )
    throw invalidPresentation();
  if (
    !v.texts.every(
      (t) =>
        record(t) &&
        Object.keys(t).every((k) =>
          [
            'purpose',
            'language',
            'item_key',
            'value',
            'source_order',
            'component_key',
            'group_ref',
            'media_ref',
            'source_item_key',
          ].includes(k),
        ) &&
        ['component_key', 'source_item_key'].every(
          (k) => !(k in t) || t[k] === null || typeof t[k] === 'string',
        ) &&
        ['group_ref', 'media_ref'].every(
          (k) =>
            !(k in t) ||
            t[k] === null ||
            (record(t[k]) &&
              exact(
                t[k],
                k === 'group_ref'
                  ? ['purpose', 'language', 'group_key']
                  : ['purpose', 'language', 'group_key', 'source_item_key'],
              ) &&
              typeof t[k].purpose === 'string' &&
              ['ko', 'en', 'und'].includes(String(t[k].language)) &&
              typeof t[k].group_key === 'string' &&
              (k === 'group_ref' || typeof t[k].source_item_key === 'string')),
        ) &&
        typeof t.purpose === 'string' &&
        t.purpose in purposes &&
        ['ko', 'en', 'und'].includes(String(t.language)) &&
        typeof t.item_key === 'string' &&
        (t.value === null || typeof t.value === 'string') &&
        (t.source_order === null || Number.isSafeInteger(t.source_order)),
    )
  )
    throw invalidPresentation();
  if (
    !v.components.every(
      (c) =>
        record(c) &&
        Object.keys(c).every((k) =>
          [
            'key',
            'kind',
            'parent_key',
            'source_order',
            'provenance',
            'line_code',
            'is_active',
            'show_on_detail',
            'show_on_search',
          ].includes(k),
        ) &&
        (c.provenance === null ||
          (record(c.provenance) &&
            exact(c.provenance, ['source_table', 'record_key']) &&
            typeof c.provenance.source_table === 'string' &&
            typeof c.provenance.record_key === 'string')) &&
        (!('line_code' in c) || c.line_code === null || typeof c.line_code === 'string') &&
        typeof c.key === 'string' &&
        ['COMMON_AREA', 'LOCATION_BULLET', 'LOCATION_LINE', 'FLOOR_DIRECTORY'].includes(
          String(c.kind),
        ) &&
        (c.parent_key === null || typeof c.parent_key === 'string') &&
        (c.source_order === null || Number.isSafeInteger(c.source_order)) &&
        ['is_active', 'show_on_detail', 'show_on_search'].every(
          (k) => !(k in c) || c[k] === null || typeof c[k] === 'boolean',
        ),
    )
  )
    throw invalidPresentation();
  if (
    !record(v.impact) ||
    !exact(v.impact, ['visible_media_assignments', 'component_reference_collections']) ||
    !Object.values(v.impact).every((n) => Number.isSafeInteger(n) && Number(n) >= 0) ||
    !['native_change', 'presentation_change'].every(
      (k) =>
        v[k] === null ||
        (record(v[k]) &&
          exact(v[k], ['actor_user_id', 'reason']) &&
          isUuid(v[k].actor_user_id) &&
          typeof v[k].reason === 'string'),
    )
  )
    throw invalidPresentation();
  return v as unknown as PresentationCurrent;
}
export async function readPresentation(client: ServiceClient, resource: Resource, id: string) {
  return parsePresentation(
    await client.get<unknown>(`admin/${resource}/${id}/presentation`, {
      headers: { 'Cache-Control': 'no-store' },
    }),
    resource,
    id,
  );
}
export async function writePresentation(
  client: ServiceClient,
  resource: Resource,
  id: string,
  body: PresentationBody,
) {
  const v = await client.put<unknown>(`admin/${resource}/${id}/presentation`, { json: body });
  if (
    !record(v) ||
    !exact(v, [
      'command_id',
      'target_kind',
      'target_id',
      'effect',
      'received_at',
      'owner_revision',
      'presentation_id',
      'presentation_revision',
    ]) ||
    v.command_id !== body.command_id ||
    v.target_id !== id ||
    v.target_kind !== kinds[resource] ||
    !['CHANGED', 'NO_CHANGE'].includes(String(v.effect)) ||
    !integer(v.owner_revision)
  )
    throw invalidPresentation();
}
export function patchText(t: TextValue, operation: 'SET' | 'REMOVE'): TextPatch {
  return {
    operation,
    identity: {
      purpose: t.purpose,
      language: t.language,
      item_key: t.item_key,
      component_key: t.component_key ?? null,
      group_ref: null,
      media_ref: null,
    },
    value:
      operation === 'REMOVE'
        ? null
        : {
            purpose: t.purpose,
            language: t.language,
            item_key: t.item_key,
            value: t.value,
            source_order: t.source_order,
            component_key: t.component_key ?? null,
            group_ref: null,
            media_ref: null,
            source_item_key: null,
          },
  };
}
